import React, { useEffect, useMemo, useState } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import '../pagination/pagination.css';
import { IoAddSharp } from 'react-icons/io5';
import Pagination from '../pagination/pagination';
import WindowedSelect from 'react-windowed-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import NoAccess from '../../../Routes/noAccess';
import api from '../../services/miscellaneousReceiptRAApi';
import LeaseApiService from '../../services/apiService';
import { fmt3 } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import { parseApiDate, selectStyles, toApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';
import MiscellaneousReceiptRAViewDialog from './MiscellaneousReceiptRAViewDialog';
import { MISC_RECEIPT_CREATE, readPermission } from './miscReceiptConstants';

const PAGE_SIZE = 50;
// Same list as the create form so the session-cached dropdowns are shared.
const DROPDOWN_TYPES = ['Properties', 'Tenants', 'PaymentTypes', 'TransactionTypes', 'DrawnOnBanks'];

const defaultFilters = () => ({
    receiptNo: '',
    tenantNo: '',
    propertyNo: '',
    unitNo: '',
    leaseNo: '',
    transactionType: '',
    paymentTypeDesc: '',
    fromDate: moment().startOf('month').format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
});

// The query string is the source of truth, so Back and refresh restore the same view.
const filtersFromParams = (params) => {
    const d = defaultFilters();
    const out = {};
    Object.keys(d).forEach((key) => {
        out[key] = params.has(key) ? params.get(key) : d[key];
    });
    return out;
};

const ViewCell = ({ data, context }) => {
    if (!data || data.isTotal) return null;
    return (
        <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={() => context.onView(data)}>
            View
        </button>
    );
};

const MiscellaneousReceiptRAList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [permissions] = useState(() => readPermission());
    const [FilterTab, setFilterTab] = useState(true);
    const [loading, setLoading] = useState(true);
    const [rowData, setRowData] = useState([]);
    const [pagination, setPagination] = useState();
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [highlight] = useState(location.state?.highlightReceiptNo || '');
    const [viewing, setViewing] = useState(null);
    const [unitList, setUnitList] = useState([]);
    const [dropdowns, setDropdowns] = useState({ properties: [], tenants: [], paymentTypes: [], transactionTypes: [] });

    const applied = useMemo(() => filtersFromParams(searchParams), [searchParams]);
    const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);
    const [draft, setDraft] = useState(applied);

    // Keep the form in step with the URL (Back / Forward / Reset).
    useEffect(() => {
        setDraft(applied);
    }, [applied]);

    // Units depend on the chosen property, same source as the Lease Receipt search.
    useEffect(() => {
        if (!draft.propertyNo) { setUnitList([]); return undefined; }
        let cancelled = false;
        (async () => {
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LRSearchUnitNosByPropertyId');
            params.append('CascadValue', draft.propertyNo);
            const res = await new LeaseApiService().getApiParamater('CASCAD_DROP', `?${params.toString()}`);
            if (!cancelled) setUnitList(res?.data?.items || []);
        })();
        return () => { cancelled = true; };
    }, [draft.propertyNo]);

    useEffect(() => {
        (async () => {
            const res = await api.getDropdowns(DROPDOWN_TYPES);
            if (!res.ok) { toast.error(res.message); return; }
            const d = res.dropdowns || {};
            setDropdowns({
                properties: d.Properties || [],
                tenants: d.Tenants || [],
                paymentTypes: d.PaymentTypes || [],
                transactionTypes: d.TransactionTypes || [],
            });
        })();
    }, []);

    // `refresh` in the router state is set after a create so returning here always refetches.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await api.getReceipts({ ...applied, pageNo: page, pageSize });
            if (cancelled) return;
            setLoading(false);
            if (res.status === 204) {
                setRowData([]);
                setPagination({ currentPage: 1, totalPages: 1, totalRecords: 0 });
            } else if (res.ok) {
                const rows = res.result || [];
                setRowData(rows);
                const totalRecords = rows[0]?.totalRecords ?? rows.length;
                setPagination(res.pagination || { currentPage: page, totalPages: Math.max(Math.ceil(totalRecords / pageSize), 1), totalRecords });
            } else {
                setRowData([]);
                toast.error(res.message);
            }
        })();
        return () => { cancelled = true; };
    }, [applied, page, pageSize, location.state?.refresh]);

    const writeParams = (filters, pageNo) => {
        const next = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && `${value}`.trim() !== '') next.set(key, `${value}`.trim());
        });
        next.set('page', pageNo);
        setSearchParams(next);
    };

    const handleSearch = () => writeParams(draft, 1);
    const handleReset = () => writeParams(defaultFilters(), 1);
    const handlePage = (pageNo) => writeParams(applied, pageNo);
    const setDraftField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const columnDefs = useMemo(() => [
        {
            headerName: '', width: 80, pinned: 'left', suppressMovable: true, sortable: false,
            cellRenderer: ViewCell,
        },
        { headerName: 'Receipt No', field: 'receiptNo', filter: true, floatingFilter: true, width: 130, pinned: 'left', suppressMovable: true },
        {
            // paymentDate is a legacy display string; sort and format from the real date.
            headerName: 'Date', field: 'paymentDateValue', filter: true, floatingFilter: true, width: 120, suppressMovable: true,
            valueFormatter: (p) => {
                const d = parseApiDate(p.value);
                return d ? moment(d).format('DD-MMM-YYYY') : '';
            },
        },
        { headerName: 'Tenant', field: 'tenantName', filter: true, floatingFilter: true, width: 200, suppressMovable: true },
        { headerName: 'Property', field: 'propertyName', filter: true, floatingFilter: true, width: 180, suppressMovable: true },
        { headerName: 'Unit', field: 'unitNo', filter: true, floatingFilter: true, width: 100, suppressMovable: true },
        { headerName: 'Transaction Type', field: 'transactionType', filter: true, floatingFilter: true, width: 150, suppressMovable: true },
        { headerName: 'Cheque No', field: 'chequeNo', filter: true, floatingFilter: true, width: 120, suppressMovable: true },
        {
            headerName: 'Debit Account', field: 'debitAccountName', filter: true, floatingFilter: true, width: 200, suppressMovable: true,
            valueGetter: (p) => p.data?.debitAccountName || p.data?.debitAccountCode,
        },
        { headerName: 'Lines', field: 'lineCount', width: 90, suppressMovable: true, type: 'rightAligned' },
        {
            headerName: 'Total Amount', field: 'totalAmount', filter: true, floatingFilter: true, width: 140, suppressMovable: true, type: 'rightAligned',
            valueFormatter: (p) => (p.value === null || p.value === undefined ? '' : fmt3(p.value)),
        },
        { headerName: 'Ref No', field: 'refNo', filter: true, floatingFilter: true, width: 120, suppressMovable: true },
        { headerName: 'Created By', field: 'modifiedBy', filter: true, floatingFilter: true, width: 140, suppressMovable: true },
    ], []);

    const pageTotal = useMemo(() => rowData.reduce((sum, r) => sum + (Number(r.totalAmount) || 0), 0), [rowData]);
    const pinnedBottomRowData = useMemo(
        () => (rowData.length ? [{ receiptNo: 'Page Total', totalAmount: pageTotal, isTotal: true }] : []),
        [rowData, pageTotal],
    );

    const propertyOptions = dropdowns.properties.map((p) => ({ value: p.value, label: p.text }));
    const tenantOptions = dropdowns.tenants.map((t) => ({ value: t.value, label: `${t.value} - ${t.text}` }));
    const unitOptions = unitList
        .slice()
        .sort((a, b) => `${a.text}`.localeCompare(`${b.text}`, undefined, { numeric: true, sensitivity: 'base' }))
        .map((u) => ({ value: u.text, label: u.text }));
    const typeOptions =dropdowns.transactionTypes.map((t) => ({ value: t.text, label: t.text }));
    // The server filters on the payment type description, so the text is the value.
    const paymentOptions = dropdowns.paymentTypes.map((p) => ({ value: p.text, label: p.text }));
    const pick = (options, value) => (value ? options.find((o) => o.value === value) || { value, label: value } : null);
    const select = (label, options, key, extra = {}) => (
        <div>
            <label className="form-label">{label}</label>
            <WindowedSelect
                className="p-0"
                theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                styles={selectStyles}
                isClearable
                value={pick(options, draft[key])}
                onChange={(opt) => setDraftField(key, opt?.value || '')}
                options={options}
                {...extra}
            />
        </div>
    );

    return (
        <div className="p-3">
            {permissions?.canView === true ? (
                <>
                    <div className="cost-center-section">
                        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                            <div className="param-label pt-1">Search Parameters</div>
                        </div>
                        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
                            <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                                    <div>
                                        <label className="form-label">Receipt No</label>
                                        <input
                                            type="text" className="p-2 form-control" placeholder="Receipt No"
                                            value={draft.receiptNo} onChange={(e) => setDraftField('receiptNo', e.target.value)}
                                        />
                                    </div>
                                    {select('Tenant', tenantOptions, 'tenantNo')}
                                    {select('Property', propertyOptions, 'propertyNo', {
                                        onChange: (opt) => setDraft((prev) => ({ ...prev, propertyNo: opt?.value || '', unitNo: '' })),
                                    })}
                                    {select('Unit No', unitOptions, 'unitNo', {
                                        placeholder: draft.propertyNo ? 'Select...' : 'Select Property first',
                                        isDisabled: !draft.propertyNo,
                                    })}
                                    <div>
                                        <label className="form-label">Lease No</label>
                                        <input
                                            type="text" className="p-2 form-control" placeholder="Lease No"
                                            value={draft.leaseNo} onChange={(e) => setDraftField('leaseNo', e.target.value)}
                                        />
                                    </div>
                                    {select('Transaction Type', typeOptions, 'transactionType')}
                                    {select('Payment Type', paymentOptions, 'paymentTypeDesc')}
                                    <div>
                                        <label className="form-label">From Date</label>
                                        <DatePicker
                                            selected={parseApiDate(draft.fromDate)}
                                            onChange={(d) => setDraftField('fromDate', toApiDate(d) || '')}
                                            dateFormat="dd-MMM-yyyy" className="p-2 form-control w-100" isClearable
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">To Date</label>
                                        <DatePicker
                                            selected={parseApiDate(draft.toDate)}
                                            onChange={(d) => setDraftField('toDate', toApiDate(d) || '')}
                                            dateFormat="dd-MMM-yyyy" className="p-2 form-control w-100" isClearable
                                        />
                                    </div>
                                    <div className="d-flex gap-2 align-items-end">
                                        <div onClick={handleSearch} className="mainbtnGry flex-end gap-2 w-50">
                                            <FaFileCircleExclamation color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Search</div>
                                        </div>
                                        <div onClick={handleReset} className="mainbtnRed flex-end gap-2 w-50">
                                            <RiRefreshLine color="#fff" className="center mt-1 mx-2" />
                                            <div className="center">Reset</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-box2 p-3 mt-2">
                        <div className="d-flex justify-contant-between">
                            <h5 className="col mainInvHead">Miscellaneous Receipt List</h5>
                            <div className="d-flex justify-content-end gap-1">
                                {permissions?.canAdd === true && (
                                    <div className="col center">
                                        <button className="mainbtn" type="button" onClick={() => navigate(MISC_RECEIPT_CREATE)}>
                                            <IoAddSharp color="#fff" className="center mt-1" /> <div>New Receipt</div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {loading ? (
                            <div>Loading...</div>
                        ) : rowData.length === 0 ? (
                            <div className="text-center text-muted p-4">No miscellaneous receipts found for the selected filters.</div>
                        ) : (
                            <div className="gridshad mt-2">
                                <div className="ag-theme-quartz" style={{ height: 650 }}>
                                    <AgGridReact
                                        getRowStyle={(params) => {
                                            if (params.data?.isTotal) return { background: '#E8E4F0', fontWeight: 'bold' };
                                            if (highlight && params.data?.receiptNo === highlight) return { background: '#FFF3C4' };
                                            return params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' };
                                        }}
                                        rowData={rowData}
                                        enableFilter={true}
                                        floatingFilter={true}
                                        columnDefs={columnDefs}
                                        pinnedBottomRowData={pinnedBottomRowData}
                                        context={{ onView: (row) => setViewing(row.receiptNo) }}
                                    />
                                </div>
                                <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={handlePage} />
                            </div>
                        )}
                    </div>
                    {viewing && <MiscellaneousReceiptRAViewDialog receiptNo={viewing} onClose={() => setViewing(null)} />}
                </>
            ) : (
                <NoAccess />
            )}
        </div>
    );
};

export default MiscellaneousReceiptRAList;
